import type { CSSProperties } from 'react';

import type { ImageBlockSettings, ShapeBlockSettings, TextBlockSettings } from 'services/entities/BlockTypes';
import type { ImageDimension } from 'components/editor/GridDndEditor/Block/Image/GridImageBlock/ImageComponent';
import ImageComponent, { resolveObjectFit } from 'components/editor/GridDndEditor/Block/Image/GridImageBlock/ImageComponent';
import { PreviewBlock } from 'components/editor/GridDndEditor/Block/Preview/PreviewBlock';
import { getEditorMaxHeight } from 'components/editor/GridDndEditor/gridHelper';
import TipTapTextblockPreview from 'components/editor/GridDndEditor/TiptapEditor/components/TipTapTextblockPreview';
import { GridBlockType } from 'services/entities/BlockTypes';
import { getPageSizeForOrientation, gridPixelSize } from 'components/editor/shared/gridConfig';
import type { PageOrientation, PageSize } from 'components/editor/shared/gridConfig';
import { getBlocksContentFromApiResponse } from 'components/preview/PreviewSections';
import { PreviewVideo } from 'components/preview/previewVideo';
import { PreviewEmbed } from 'components/preview/PreviewEmbed';
import { SectionTitleTypography } from 'components/preview/SectionTitleTypography';
import { PREVIEW_SECTION_CONTAINER_CLASS } from 'components/preview/useTrackActiveSectionInPreview';
import { usePreviewContentVariables } from 'providers/PreviewContentVariablesContext';
import { Suspense } from 'react';
import type { SectionByDocumentIdApiResponse } from 'services/documents/DocumentRepository';
import { PreviewContentTableBlock } from './PreviewContentTableBlock';
import { ShapeType } from '../../components/editor/grid/reduxStore/blockStyleSettingsSlice.ts';
import { DEFAULT_SHAPE_BACKGROUND_COLOR } from 'components/editor/shared/documentColorDefaults';
import Square from '../../components/editor/GridDndEditor/Block/Shape/SquareShapeComponent.tsx';
import Circle from '../../components/editor/GridDndEditor/Block/Shape/CircleShapeComponent.tsx';
import Triangle from '../../components/editor/GridDndEditor/Block/Shape/TriangleShapeComponent.tsx';
import { DynamicIcon } from '../../components/editor/GridDndEditor/Block/Shape/DynamicICon.tsx';
import TextWrapContainer from 'components/editor/GridDndEditor/Block/Text/TextWrapContainer/index.tsx';
import { useMobileZoomStyles } from 'components/preview/useMobileZoomStyles.ts';

const PreviewContentSections = ({
  documentStyle,
  sections,
  isDocumentLocked,
  isTemplate,
}: {
  documentStyle: Record<string, any>;
  sections: SectionByDocumentIdApiResponse[];
  isDocumentLocked: boolean;
  isTemplate?: boolean;
}) => {
  const { width: canvasWidth, height: canvasMinHeight } =
    (documentStyle.pageSize as PageSize | undefined) ??
    getPageSizeForOrientation(undefined, documentStyle.pageOrientation as PageOrientation | undefined);
  const { wrapperStyle, innerStyle, getScaledHeight } = useMobileZoomStyles(canvasWidth);
  return (
    <>
      {sections.map((section, sectionIndex) => {
        const { gridBlocksContent, blockStyleMap } = getBlocksContentFromApiResponse(section.blocks);
        const basePageHeight = Math.max(canvasMinHeight, getEditorMaxHeight(gridBlocksContent));

        const pageHeight = getScaledHeight(basePageHeight);
        return (
          <div
            key={`preview-section-container-${section.id}`}
            id={section.id}
            className={PREVIEW_SECTION_CONTAINER_CLASS}
            data-section-id={section.id}
          >
            <SectionTitleTypography sectionId={section.id} sectionTitle={section.title} />
            <div style={{ ...wrapperStyle, height: pageHeight }}>
              <div
                key={`preview-section-${section.id}`}
                data-testid="grid-preview"
                className={`preview__editor__wrapper__grid section-${sectionIndex}`}
                style={
                  {
                    '--canvas-width': `${canvasWidth}px`,
                    '--canvas-min-height': `${canvasMinHeight}px`,
                    backgroundColor: documentStyle.backgroundColor,
                    height: basePageHeight,
                    ...innerStyle,
                  } as CSSProperties
                }
              >
                <div className="preview__grid__blocks">
                  {Object.values(gridBlocksContent).map((block) => {
                    if (block.type === GridBlockType.TEXT) {
                      const blockSettings = blockStyleMap.get(block.blockConfig.id);
                      if (!blockSettings) return;
                      const {
                        paddingLeft,
                        paddingTop,
                        paddingRight,
                        paddingBottom,
                        backgroundColor,
                        borderColor,
                        borderLeft,
                        borderRight,
                        borderTop,
                        borderBottom,
                        borderRadius,
                      } = blockSettings as unknown as TextBlockSettings;
                      const styles = {
                        borderTop: `solid ${borderColor} ${borderTop}px`,
                        borderBottom: `solid ${borderColor} ${borderBottom}px`,
                        borderLeft: `solid ${borderColor} ${borderLeft}px`,
                        borderRight: `solid ${borderColor} ${borderRight}px`,
                        borderRadius: `${borderRadius}px`,
                        backgroundColor: backgroundColor,
                        paddingLeft: `${paddingLeft}px`,
                        paddingTop: `${paddingTop}px`,
                        paddingRight: `${paddingRight}px`,
                        paddingBottom: `${paddingBottom}px`,
                        height: '100%',
                      };

                      return (
                        <PreviewBlock
                          key={`preview-section-block-${block.blockConfig.id}`}
                          gridBlock={block}
                          gridSystemInPx={gridPixelSize}
                        >
                          <div
                            className={block.blockConfig.id}
                            data-testid={`test_${block.blockConfig.id}`}
                            style={{
                              width: '100%',
                              height: '100%',
                            }}
                          >
                            <TextWrapContainer
                              borderRadius={borderRadius}
                              blockContent={block}
                              borderWidth={{ borderTop, borderBottom, borderLeft, borderRight }}
                            >
                              <Suspense fallback={<p>Loading...</p>}>
                                <TipTapTextblockPreview
                                  key={block.blockConfig.id}
                                  blockId={block.blockConfig.id}
                                  documentContent={block.content}
                                  usePreviewVariables={usePreviewContentVariables}
                                  isTemplate={isTemplate}
                                  style={styles}
                                />
                              </Suspense>
                            </TextWrapContainer>
                          </div>
                        </PreviewBlock>
                      );
                    } else if (block.type === GridBlockType.IMAGE) {
                      const blockSettings = blockStyleMap.get(block.blockConfig.id);
                      if (!blockSettings) return;
                      const imageAlt = 'imageAlt' in blockSettings ? blockSettings.imageAlt : '';
                      const imageLink = 'imageLink' in blockSettings && blockSettings.imageLink ? 'https://' + blockSettings.imageLink : '';
                      const {
                        paddingLeft,
                        paddingTop,
                        paddingRight,
                        paddingBottom,
                        borderColor,
                        borderLeft,
                        borderRight,
                        borderTop,
                        borderBottom,
                        borderRadius,
                        opacity,
                        top: imageTop,
                        objectFit,
                        objectPosition,
                      } = blockSettings as unknown as ImageBlockSettings;
                      const resolvedObjectFit = resolveObjectFit(objectFit);

                      const imageDimension: ImageDimension = {
                        width: block.blockConfig.width,
                        height: block.blockConfig.height,
                      };
                      return (
                        <PreviewBlock key={block.blockConfig.id} gridBlock={block} gridSystemInPx={gridPixelSize}>
                          <div style={{ position: 'relative', overflow: 'hidden', width: '100%', height: '100%' }}>
                            {imageLink ? (
                              <a href={imageLink} target="_blank" rel="noopener">
                                <ImageComponent
                                  style={{
                                    borderTop: `solid ${borderColor} ${borderTop}px`,
                                    borderBottom: `solid ${borderColor} ${borderBottom}px`,
                                    borderLeft: `solid ${borderColor} ${borderLeft}px`,
                                    borderRight: `solid ${borderColor} ${borderRight}px`,
                                    borderRadius: `${borderRadius}px`,
                                    paddingLeft: `${paddingLeft}px`,
                                    paddingTop: `${paddingTop}px`,
                                    paddingRight: `${paddingRight}px`,
                                    paddingBottom: `${paddingBottom}px`,
                                    position: 'absolute',
                                    top: imageTop ?? 0,
                                    objectFit: resolvedObjectFit,
                                    objectPosition,
                                  }}
                                  imageAlt={imageAlt}
                                  opacity={opacity || 0}
                                  currentBlockId={block.blockConfig.id}
                                  src={block.content}
                                  dimension={imageDimension}
                                />
                              </a>
                            ) : (
                              <ImageComponent
                                style={{
                                  borderTop: `solid ${borderColor} ${borderTop}px`,
                                  borderBottom: `solid ${borderColor} ${borderBottom}px`,
                                  borderLeft: `solid ${borderColor} ${borderLeft}px`,
                                  borderRight: `solid ${borderColor} ${borderRight}px`,
                                  borderRadius: `${borderRadius}px`,
                                  paddingLeft: `${paddingLeft}px`,
                                  paddingTop: `${paddingTop}px`,
                                  paddingRight: `${paddingRight}px`,
                                  paddingBottom: `${paddingBottom}px`,
                                  position: 'absolute',
                                  top: imageTop ?? 0,
                                  objectFit: resolvedObjectFit,
                                  objectPosition,
                                }}
                                imageAlt={imageAlt}
                                opacity={opacity || 0}
                                currentBlockId={block.blockConfig.id}
                                src={block.content}
                                dimension={imageDimension}
                              />
                            )}
                          </div>
                        </PreviewBlock>
                      );
                    } else if (block.type === GridBlockType.TABLE) {
                      return (
                        <PreviewContentTableBlock
                          currencySettings={documentStyle.currencySettings}
                          key={block.blockConfig.id}
                          block={block}
                          isDocumentLocked={isDocumentLocked}
                          section={section}
                          isTemplate={isTemplate}
                        />
                      );
                    } else if (block.type === GridBlockType.VIDEO) {
                      return <PreviewVideo key={block.blockConfig.id} block={block} />;
                    } else if (block.type === GridBlockType.EMBED) {
                      return <PreviewEmbed key={block.blockConfig.id} block={block} />;
                    } else if (block.type === GridBlockType.SHAPE) {
                      const blockSettings = blockStyleMap.get(block.blockConfig.id);

                      if (!blockSettings) return;
                      const shapeType = block.content || ShapeType.SQUARE;
                      const {
                        borderColor,
                        borderLeft = 0,
                        borderRadius = 0,
                        backgroundColor = DEFAULT_SHAPE_BACKGROUND_COLOR,
                      } = (blockSettings as unknown as ShapeBlockSettings) || {};

                      const { width, height } = block.blockConfig;
                      const iconObject = {
                        [ShapeType.SQUARE]: Square,
                        [ShapeType.CIRCLE]: Circle,
                        [ShapeType.TRIANGLE]: Triangle,
                      };

                      return (
                        <PreviewBlock key={block.blockConfig.id} gridBlock={block} gridSystemInPx={gridPixelSize}>
                          <DynamicIcon
                            width={width}
                            height={height}
                            borderRadius={borderRadius}
                            lockAspectRatio={false}
                            icon={iconObject[shapeType] || Square}
                            fill={backgroundColor}
                            stroke={borderColor}
                            strokeWidth={borderLeft / 10}
                          />
                        </PreviewBlock>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default PreviewContentSections;
